import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

type UseExpandableTextOptions = {
  text: string;
  collapsedLines: number;
};

type UseExpandableTextResult = {
  contentRef: RefObject<HTMLDivElement>;
  expanded: boolean;
  isOverflowing: boolean;
  showToggle: boolean;
  toggleExpanded: () => void;
};

const hasOverflow = (element: HTMLElement) =>
  element.scrollHeight > element.clientHeight + 1;

export const useExpandableText = ({
  text,
  collapsedLines,
}: UseExpandableTextOptions): UseExpandableTextResult => {
  const contentRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const measureOverflow = useCallback(() => {
    const element = contentRef.current;
    if (!element) {
      return;
    }

    const previousDisplay = element.style.display;
    const previousOverflow = element.style.overflow;
    const previousWebkitLineClamp = element.style.webkitLineClamp;
    const previousWebkitBoxOrient = element.style.webkitBoxOrient;

    element.style.display = '-webkit-box';
    element.style.webkitBoxOrient = 'vertical';
    element.style.webkitLineClamp = `${collapsedLines}`;
    element.style.overflow = 'hidden';

    const overflowing = hasOverflow(element);

    element.style.display = previousDisplay;
    element.style.overflow = previousOverflow;
    element.style.webkitLineClamp = previousWebkitLineClamp;
    element.style.webkitBoxOrient = previousWebkitBoxOrient;

    setIsOverflowing(overflowing);

    if (!overflowing) {
      setExpanded(false);
    }
  }, [collapsedLines]);

  const toggleExpanded = useCallback(() => {
    measureOverflow();
    setExpanded((currentExpanded) => !currentExpanded);
  }, [measureOverflow]);

  useEffect(() => {
    setExpanded(false);
    setIsOverflowing(false);
  }, [text]);

  useLayoutEffect(() => {
    measureOverflow();
  }, [measureOverflow, text]);

  useEffect(() => {
    const handleWindowResize = () => measureOverflow();
    window.addEventListener('resize', handleWindowResize);

    if (typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', handleWindowResize);
    }

    const element = contentRef.current;
    if (!element) {
      return () => window.removeEventListener('resize', handleWindowResize);
    }

    resizeObserverRef.current = new ResizeObserver(() => {
      measureOverflow();
    });

    resizeObserverRef.current.observe(element);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, [measureOverflow, text, collapsedLines]);

  useEffect(() => () => {
    resizeObserverRef.current?.disconnect();
  }, []);

  return {
    contentRef,
    expanded,
    isOverflowing,
    showToggle: isOverflowing,
    toggleExpanded,
  };
};

export default useExpandableText;
